import { useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import type { Box_WidgetRpcActionType as WidgetRpcActionType } from '__generated__/graphql-types';
import gql from 'graphql-tag';

import createBoxSessionForViewMutation from 'bundles/assess-common/api/CreateBoxSessionForReadOnlyView.graphql';
import type {
  BoxWidgetSessionFragmentFragment,
  CreateBoxSessionForReadOnlyViewMutation,
  CreateBoxSessionForReadOnlyViewMutationVariables,
  CreateBoxSessionForViewSuccessFragmentFragment,
} from 'bundles/assess-common/api/__generated__/CreateBoxSessionForReadOnlyView';
import type {
  OnDemandBoxViewAnnotationSessionsV1,
  OnDemandBoxViewAnnotationSessionsV1CreateForGradingMutationVariables,
} from 'bundles/naptimejs/resources/__generated__/OnDemandBoxViewAnnotationSessionsV1';

export const onDemandBoxViewAnnotationSessionsQuery = gql`
  query onDemandBoxViewAnnotationSessionsQuery(
    $body: String!
    $itemId: String!
    $reviewResponseId: String!
    $courseId: String!
    $userId: Number!
    $action: String!
    $annotatorProfileId: String!
  ) {
    onDemandBoxViewAnnotationSessions(
      body: $body
      itemId: $itemId
      reviewResponseId: $reviewResponseId
      courseId: $courseId
      userId: $userId
      action: $action
      annotatorProfileId: $annotatorProfileId
    )
      @rest(
        type: "RestOnDemandBoxViewAnnotationSessionsV1"
        path: "onDemandBoxViewAnnotationSessions.v1?action={args.action}&userId={args.userId}&courseId={args.courseId}&reviewResponseId={args.reviewResponseId}&itemId={args.itemId}{args.annotatorProfileId}"
        method: "POST"
        bodyKey: "body"
      ) {
      session @type(name: "RestOnDemandBoxViewAnnotationSessionsSession")
      sessionId
      fileId
    }
  }
`;

type OnDemandBoxViewAnnotationSessionsV1QueryResult = {
  onDemandBoxViewAnnotationSessions: OnDemandBoxViewAnnotationSessionsV1;
};

type OnDemandBoxViewAnnotationSessionsV1QueryVariables =
  OnDemandBoxViewAnnotationSessionsV1CreateForGradingMutationVariables & {
    action: string;
    body: {};
  };

const useCreateBoxDocumentAnnotationSession = () => {
  const [session, setSession] = useState<BoxWidgetSessionFragmentFragment | null>(null);

  const [createBoxSessionForView, { loading: viewSessionLoading, error: viewSessionError }] = useMutation<
    CreateBoxSessionForReadOnlyViewMutation,
    CreateBoxSessionForReadOnlyViewMutationVariables
  >(createBoxSessionForViewMutation, {
    context: { clientName: 'gatewayGql' },
    // handling data onComplete to ensure session state update triggers re-render async
    onCompleted: (data) => {
      const sessionData = (data?.Submission_CreateBoxSessionForView as CreateBoxSessionForViewSuccessFragmentFragment)
        ?.session;
      if (sessionData) {
        setSession(sessionData);
      }
    },
    onError: () => {
      setSession(null);
    },
  });

  const [createBoxSessionForGrading, { loading: gradingSessionLoading, error: gradingSessionError }] = useLazyQuery<
    OnDemandBoxViewAnnotationSessionsV1QueryResult,
    OnDemandBoxViewAnnotationSessionsV1QueryVariables
  >(onDemandBoxViewAnnotationSessionsQuery, {
    onCompleted: (data) => {
      const { session: sessionData, fileId, sessionId: id } = data?.onDemandBoxViewAnnotationSessions || {};
      if (sessionData && fileId && id) {
        setSession({
          // default to 1 hour from now for session expiration, however normally this will be given
          expiresAt: sessionData.expiresAt
            ? Math.floor(new Date(sessionData.expiresAt).getTime() / 1000).toString()
            : Math.floor((Date.now() + 3600000) / 1000).toString(),
          fileId,
          id,
          iframeTitle: sessionData.iframeTitle || '',
          rpcActionTypes: (sessionData.rpcActionTypes as WidgetRpcActionType[]) || [],
          sandbox: sessionData.sandbox,
          source: sessionData.src,
        });
      } else {
        setSession(null);
      }
    },
    onError: () => {
      setSession(null);
    },
  });

  return {
    session,
    createBoxSessionForView,
    createBoxSessionForViewStatus: {
      loading: viewSessionLoading,
      error: viewSessionError,
    },
    createBoxSessionForGrading,
    createBoxSessionForGradingStatus: {
      loading: gradingSessionLoading,
      error: gradingSessionError,
    },
  };
};

export default useCreateBoxDocumentAnnotationSession;
